(function (scope) {
  'use strict';
  const jawNumbers = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, eight: 8 };
  const words = (value = '') => String(value).toLowerCase().replace(/[\u00ae\u2122]/g, '').replace(/[^a-z0-9]+/g, ' ').trim()
    .replace(/\b(one|two|three|four|five|six|eight)(?= jaws?\b)/g, (number) => jawNumbers[number])
    .replace(/\broundover\b/g, 'round over');
  const code = (value = '') => words(value).replace(/ /g, '');
  const escapePattern = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parseRules = (text = '') => String(text).split(/\r?\n/).flatMap((line) => {
    const [from, to, extra] = line.split('=>').map((part) => part.trim());
    if (!from || !to || extra) return [];
    return from.split('|').map((term) => ({ from: words(term), to })).filter((rule) => rule.from);
  }).sort((a, b) => b.from.length - a.from.length);
  const singular = (word) => word.endsWith('ies') ? `${word.slice(0, -3)}y` : /ss$/.test(word) ? word : word.replace(/s$/, '');
  const families = { sanding: 'abrasive', sandpaper: 'abrasive', abrasives: 'abrasive', waterstone: 'stone', waterstones: 'stone', routing: 'router', sawing: 'saw', drilling: 'drill' };
  const token = (word) => families[word] || singular(word);
  const tokens = (value) => words(value).split(' ').filter((word) => word && !['the', 'a', 'an', 'and', 'for', 'with', 'of', 'in', 'to'].includes(word)).map(token);
  const nearWord = (a, b) => {
    if (a === b || (a.length >= 3 && b.startsWith(a))) return true;
    // One typo/transposition in a word, never in a model number or dimension.
    if (Math.min(a.length, b.length) < 4 || /\d/.test(a + b) || Math.abs(a.length - b.length) > 1) return false;
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
      if (a[i] === b[i]) continue;
      return a.slice(i + 1) === b.slice(i + 1) || a.slice(i) === b.slice(i + 1) || a.slice(i + 1) === b.slice(i)
        || (a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2));
    }
    return true;
  };
  const dimensions = (value) => [...String(value).toLowerCase().matchAll(/\b(\d+(?:\.\d+)?)\s*(mm|cm|inch(?:es)?|in\b|["\u2033])/g)]
    .map((match) => Math.round(Number(match[1]) * (match[2] === 'mm' ? 1 : match[2] === 'cm' ? 10 : 25.4) * 100) / 100);

  function create(config = {}) {
    const rules = parseRules(config.aliases);
    const normalize = (value = '') => {
      let result = String(value).trim().replace(/^sku\s*[:#]?\s+/i, '');
      // Rules operate on whole words/phrases and are applied once, not recursively.
      const normalized = words(result);
      const exact = rules.find((rule) => rule.from === normalized);
      if (exact) return exact.to;
      rules.forEach((rule) => { result = result.replace(new RegExp(`(^|[^a-z0-9])${rule.from.split(' ').map(escapePattern).join('[\\s-]+')}(?=$|[^a-z0-9])`, 'gi'), (_, before) => before + rule.to); });
      return result.trim();
    };
    const isCode = (value) => {
      const query = normalize(value);
      const compact = code(query);
      if (/^\d+(?:\.\d+)?\s*(?:mm|cm|m|inch|inches|in|hp|v|w|kw|ml|l|mt)$/i.test(query)) return false;
      if (/\b(?:mm|cm|inch|inches|hp|volt|grit)\b/i.test(query)) return false;
      return compact.length >= 3 && compact.length <= 32 && /\d/.test(compact) && /^[a-z0-9\s._/*-]+$/i.test(query)
        && (query.trim().split(/\s+/).length <= 2) && !/^\d{1,2}$/.test(compact);
    };
    const intent = (value) => {
      const query = words(value).replace(/\bharrison s\b/g, 'harrisons');
      if (/\baddress (?:labels?|numbers?|plaques?|plates?|signs?|stamps?)\b/.test(query)) return null;
      if (/\b(?:address|adress|addres|opening hours|opening times|trading hours|store hours|shop hours)\b/.test(query)
        || /^(?:location(?: and opening hours)?|directions|map|find us|where are you|are you open(?: today)?|when are you open|contact|contact us|phone number)$/.test(query)
        || /\b(?:store|shop|showroom|harrisons(?: hardware)?|pick up) (?:location|directions|map|hours)\b/.test(query)
        || /\b(?:find|visit|get to) (?:your |the )?(?:store|shop|showroom|harrisons|you)\b/.test(query)) return { key: 'contact', label: config.contactLabel || 'Store address, opening hours & contact', url: config.contactUrl || '/pages/contact' };
      if (/^(?:(?:your|the) )?(?:shipping|delivery|freight)(?: (?:information|info|cost|costs|rates|policy|options|time|times))?$/.test(query)
        || /^(?:how much is (?:shipping|delivery|freight)|do you deliver|delivery and pickup)$/.test(query)) return { key: 'delivery', label: config.deliveryLabel || 'Delivery information', url: config.deliveryUrl || config.contactUrl || '/pages/contact' };
      if (/^(?:returns?|refunds?)(?: (?:policy|information|info))?$/.test(query)) return { key: 'returns', label: config.returnsLabel || 'Returns information', url: config.returnsUrl || config.contactUrl || '/pages/contact' };
      return null;
    };
    const skuMatches = (entries, value) => {
      const needle = code(normalize(value));
      if (!isCode(value)) return [];
      return entries.filter((entry) => code(entry.sku).includes(needle)).map((entry) => {
        const full = code(entry.sku);
        const model = code(entry.sku.replace(/^[a-z]+[-_]/i, ''));
        return { ...entry, score: full === needle ? 500 : model === needle ? 450 : model.startsWith(needle) ? 400 : full.startsWith(needle) ? 350 : 300 };
      }).sort((a, b) => b.score - a.score);
    };
    const skuQuery = (matches) => [...new Set(matches.map((entry) => entry.sku))].slice(0, 30)
      .map((sku) => `variants.sku:"${sku.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`).join(' OR ');
    const assess = (product, value) => {
      const query = normalize(value);
      const queryCode = code(query);
      const skuList = String(product.searchSkus || '').split('|').filter(Boolean);
      const exactSku = isCode(value) && skuList.some((sku) => code(sku).includes(queryCode));
      if (exactSku) {
        const best = skuMatches(skuList.map((sku) => ({ sku })), value)[0];
        return { score: best.score, close: true, sku: best.sku };
      }
      const title = String(product.searchTitle || '');
      const text = `${title} ${product.searchType || ''} ${product.searchVariants || ''} ${product.searchVendor || ''}`;
      const requestedDimensions = dimensions(query);
      const productDimensions = dimensions(`${title} ${product.searchVariants || ''}`);
      if (requestedDimensions.length && !requestedDimensions.every((size) => productDimensions.includes(size))) return { score: -20, close: false };
      if (isCode(value)) {
        const modelMatch = code(title).includes(queryCode) || code(`${product.searchVendor || ''} ${title}`).includes(queryCode)
          || String(product.searchVariants || '').split('|').some((variant) => code(variant).includes(queryCode));
        if (modelMatch) return { score: 180, close: true };
      }
      const wanted = tokens(query).filter((word) => !/^(?:mm|cm|inch|inche|in)$/.test(word));
      const available = tokens(normalize(text));
      // Confirm jaw counts as a phrase, using the description only when primary fields have no count.
      const jawCount = words(query).match(/\b(\d+) jaws?\b/)?.[1];
      if (jawCount) {
        const primary = words(text);
        const evidence = /\b\d+ jaws?\b/.test(primary) ? primary : words(product.searchDescription || '');
        const confirmed = new RegExp(`\\b${jawCount} jaws?\\b`).test(evidence);
        if (!confirmed) return { score: -20, close: false };
        available.push(jawCount, 'jaw');
      }
      // Mixed queries can name a partial model and a product, in either order.
      // Match whole numeric runs (305 in M305), never a different number (3050).
      const matchesTerm = (word, candidates) => candidates.some((candidate) => /^\d+$/.test(word)
        ? (candidate.match(/\d+/g) || []).includes(word)
        : (!/\d/.test(word) || (word.match(/\d+/g) || []).join('|') === (candidate.match(/\d+/g) || []).join('|')) && nearWord(word, candidate));
      const modelTokens = tokens(skuList.join(' '));
      const matched = wanted.filter((word) => matchesTerm(word, available)
        || (/\d/.test(word) && matchesTerm(word, modelTokens)));
      const complete = wanted.length > 0 && matched.length === wanted.length;
      const titleTokens = tokens(normalize(title));
      const titleMatch = wanted.every((word) => matchesTerm(word, titleTokens));
      const phrase = words(title).includes(words(query));
      const incidental = [['cleaning stick', 'clean'], ['extension table', 'extension'], ['stop collar', 'collar'], ['chuck insert', 'insert'], ['chuck adapter', 'adapter'], ['chuck adaptor', 'adaptor']]
        .some(([name, required]) => words(title).includes(name) && !words(query).includes(required));
      if (incidental) return { score: matched.length * 5, close: false };
      return { score: complete ? (phrase ? 160 : titleMatch ? 140 : 120) : matched.length * 10, close: complete };
    };
    const rank = (products, query) => products.map((product, index) => ({ product, index, assessment: assess(product, query) }))
      .sort((a, b) => b.assessment.score - a.assessment.score || Number(b.product.searchInStock === 'true') - Number(a.product.searchInStock === 'true') || a.index - b.index);
    return { normalize, isCode, intent, skuMatches, skuQuery, assess, rank };
  }
  scope.HHSearch = { create, words, code, parseRules, dimensions };
  if (typeof module !== 'undefined' && module.exports) module.exports = scope.HHSearch;
})(typeof window === 'undefined' ? globalThis : window);
